import React, { useState, Component } from 'react';
import { ChevronDownIcon, PlusIcon, MinusIcon, XIcon, SendIcon } from 'lucide-react';
const faqs = [{
  question: 'How does Emigo work?',
  answer: "When you tap a feature like Reframe, Interpret, or Create, Emigo analyzes your message differently depending on what you need.\n\n• Reframe helps you rewrite something you've already drafted.\n\n• Interpret helps you understand or respond to someone else's message by analyzing tone, intent, and meaning.\n\n• Create helps you craft something new from scratch, like a post, text, or heartfelt message.\n\nEmigo only processes text when you use a feature, and otherwise stays completely offline."
}, {
  question: 'Does Emigo read my messages?',
  answer: 'Emigo has no access to your chats. It only analyzes the text or screenshots you choose when using a feature like Reframe or Interpret. Everything is processed securely and never tied to your identity.'
}, {
  question: 'How does Emigo handle my personal information?',
  answer: 'Emigo does not require you to create an account or provide any personal information. It has no access to your private messages or conversations. When you use a feature, only the text or screenshots you choose to analyze are securely processed to generate a response. This data is handled confidentially, used only for that request, and not stored by Emigo, sold, or linked to your identity.'
}, {
  question: 'Is my data private and secure?',
  answer: "Yes. Your data is private and secure. Emigo doesn't track, sell, or link anything to you. When you use a feature, your text is processed securely in a controlled cloud environment. Emigo does not store or save any local data on your device."
}, {
  question: 'Can Emigo further personalize my responses?',
  answer: "For now, you can personalize Emigo by using the edit feature: just tell it how you'd like a response changed, and it learns your preferences for that session. In the future, we'll introduce optional personalization that can adapt to your tone and emotional style over time, but only if you choose to turn it on."
}];
const FAQItem = ({
  question,
  answer,
  isLast
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className={`${!isLast ? 'border-b border-gray-100' : ''}`} data-id="element-62">
      <button className="flex justify-between items-start w-full text-left py-6 group hover:bg-[#FFF5EB]/30 px-4 -mx-4 rounded-lg transition-all duration-200" onClick={() => setIsOpen(!isOpen)} data-id="element-63">
        <span className="text-lg font-semibold text-gray-900 pr-8 group-hover:text-[#E87B5C] transition-colors duration-200" data-id="element-64">
          {question}
        </span>
        <div className="flex-shrink-0 mt-1" data-id="element-65">
          {isOpen ? <MinusIcon className="h-5 w-5 text-[#E87B5C]" data-id="element-66" /> : <PlusIcon className="h-5 w-5 text-[#E87B5C] group-hover:scale-110 transition-transform duration-200" data-id="element-67" />}
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-68">
        <div className="pb-6 px-4 -mx-4" data-id="element-69">
          <p className="text-gray-600 leading-relaxed whitespace-pre-line" data-id="element-70">
            {answer}
          </p>
        </div>
      </div>
    </div>;
};
const FAQSection = () => {
  const [showSupportForm, setShowSupportForm] = useState(false);
  const [supportText, setSupportText] = useState('');
  const handleOpenSupportForm = () => {
    setShowSupportForm(true);
  };
  const handleCloseSupportForm = () => {
    setShowSupportForm(false);
    setSupportText('');
  };
  const handleSubmit = e => {
    e.preventDefault();
    // Create mailto link with the support message in the body
    const mailtoLink = `mailto:info@joinemigo.com?subject=Support Request&body=${encodeURIComponent(supportText)}`;
    // Open email client with the message
    window.location.href = mailtoLink;
    // Close the form after submission
    handleCloseSupportForm();
  };
  return <section id="faq" className="py-12 md:py-16 bg-gradient-to-b from-white to-[#FFF5EB]" data-id="element-71">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-72">
        <div className="text-center mb-12" data-id="element-73">
          <h2 className="text-3xl md:text-4xl font-bold text-[#E87B5C] mb-4" data-id="element-74">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-gray-600" data-id="element-75">
            Everything you need to know about Emigo.
          </p>
        </div>

        {/* Single column layout with modern card */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8" data-id="element-76">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isLast={index === faqs.length - 1} data-id="element-77" />)}
        </div>

        {/* Still have questions CTA */}
        <div className="text-center mt-8" data-id="element-78">
          <p className="text-gray-600 mb-4" data-id="element-79">Still have questions?</p>
          <button onClick={handleOpenSupportForm} className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-br from-[#E87B5C] to-[#D16A4A] text-white font-medium hover:opacity-90 transition-all shadow-sm hover:shadow-md" data-id="element-80">
            Contact Support
          </button>
        </div>
      </div>

      {/* Support Form Popup */}
      {showSupportForm && <div className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 bg-black bg-opacity-50" data-id="element-81">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-popup" data-id="element-82">
            <div className="flex justify-between items-center mb-6" data-id="element-83">
              <h2 className="text-xl font-bold text-gray-800" data-id="element-84">
                Contact Support
              </h2>
              <button onClick={handleCloseSupportForm} className="text-gray-500 hover:text-gray-700 transition-colors" data-id="element-85">
                <XIcon className="w-5 h-5" data-id="element-86" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-87">
              <div data-id="element-88">
                <label htmlFor="support" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-89">
                  Your Message
                </label>
                <textarea id="support" value={supportText} onChange={e => setSupportText(e.target.value)} rows={6} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#E87B5C] focus:border-[#E87B5C] text-gray-800" placeholder="How can we help you?" required data-id="element-90"></textarea>
              </div>
              <div className="flex justify-end space-x-3 pt-2" data-id="element-91">
                <button type="button" onClick={handleCloseSupportForm} className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-92">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 bg-gradient-to-r from-[#E87B5C] to-[#FFD700] text-white rounded-md hover:opacity-90 transition-colors flex items-center" data-id="element-93">
                  Submit to info@joinemigo.com
                  <SendIcon className="ml-2 h-4 w-4" data-id="element-94" />
                </button>
              </div>
            </form>
          </div>
        </div>}
    </section>;
};
export default FAQSection;